import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";
import {
  Checkbox,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  TextInput,
  Typography,
} from "@/components/ui";
import { defectTypeOptions } from "@/pages/public/repairIntakeForm/repairIntakeFormConstants";
import type { RepairIntakeFormData } from "@/store/useRepairIntakeStore";

type RepairIntakeSection5Props = {
  form: UseFormReturn<RepairIntakeFormData>;
  defectTypes: string[];
  defectLengthFields: FieldArrayWithId<RepairIntakeFormData, "section5.defectLengthEntries", "id">[];
  desiredRepairLengthFields: FieldArrayWithId<RepairIntakeFormData, "section5.desiredRepairLengthEntries", "id">[];
  onToggleDefectType: (value: string) => void;
};

export function RepairIntakeSection5({
  form,
  defectTypes,
  defectLengthFields,
  desiredRepairLengthFields,
  onToggleDefectType,
}: RepairIntakeSection5Props) {
  return (
    <>
      <div className="space-y-3">
        <Typography variant="text-small" weight="medium">
          1. What Type of Defect is it *
        </Typography>
        <Typography variant="text-small" className="text-gray-600">
          Example: Pinhole / External corrosion / Internal corrosion / Pitting / General wall loss / Gouge / Dent / Weld
          anomaly / If Other please describe defect
        </Typography>
        <div className="grid gap-3 sm:grid-cols-2">
          {defectTypeOptions.map((option) => (
            <Checkbox
              key={option}
              checked={defectTypes.includes(option)}
              label={option}
              onChange={() => onToggleDefectType(option)}
            />
          ))}
          <Checkbox
            checked={defectTypes.includes("Other")}
            label="Other:"
            onChange={() => {
              onToggleDefectType("Other");
              if (defectTypes.includes("Other")) {
                form.setValue("section5.defectTypeOther", "", {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                });
              }
            }}
          />
        </div>
        {defectTypes.includes("Other") ? (
          <FormField
            control={form.control}
            name="section5.defectTypeOther"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <TextInput placeholder="Describe defect" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}
        <Typography variant="validationError">{form.formState.errors.section5?.defectTypes?.message}</Typography>
      </div>

      <div className="space-y-4">
        <div>
          <Typography variant="text-small" weight="medium">
            2. Defect Length *
          </Typography>
          <Typography variant="text-small" className="mt-1 text-gray-600">
            One row is shown for each component entered in Section 1.
          </Typography>
        </div>
        {defectLengthFields.map((field, index) => (
          <div key={field.id} className="rounded-lg border border-gray-200 p-4">
            <div className="mb-4 flex items-center justify-between gap-4">
              <Typography weight="semibold">Component {index + 1}:</Typography>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name={`section5.defectLengthEntries.${index}.diameterOrNPS`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Diameter or NPS</FormLabel>
                    <FormControl>
                      <TextInput placeholder={'e.g. 8"'} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`section5.defectLengthEntries.${index}.defectLength`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Defect Length (inches)</FormLabel>
                    <FormControl>
                      <TextInput placeholder="e.g. 12" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        ))}
        <FormField
          control={form.control}
          name="section5.holeOrSlotSize"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Size of hole or slot</FormLabel>
              <FormControl>
                <TextInput placeholder={'½" diameter or 1" × ¼" slot'} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="space-y-4">
        <div>
          <Typography variant="text-small" weight="medium">
            8. Desired Repair Length *
          </Typography>
          <Typography variant="text-small" className="mt-1 text-gray-600">
            One row is shown for each component entered in Section 1.
          </Typography>
        </div>
        {desiredRepairLengthFields.map((field, index) => (
          <div key={field.id} className="rounded-lg border border-gray-200 p-4">
            <div className="mb-4 flex items-center justify-between gap-4">
              <Typography weight="semibold">Component {index + 1}:</Typography>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name={`section5.desiredRepairLengthEntries.${index}.diameterOrNPS`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Diameter or NPS</FormLabel>
                    <FormControl>
                      <TextInput placeholder={'e.g. 8"'} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`section5.desiredRepairLengthEntries.${index}.desiredRepairLength`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Desired Repair Length (inches)</FormLabel>
                    <FormControl>
                      <TextInput placeholder="e.g. 24" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
